import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { CompendiumText } from "../components/CompendiumText";
import { TimelineSpinner } from "../components/timeline/TimelineSpinner";
import { booksInSeries, characterPath, pageTitle, SERIES, type BookEntry } from "../config/site";
import type { CompendiumEntry, CompendiumKind } from "../data/compendium";
import { describeChapterList } from "../data/key-items";
import { loadPortraitUrls } from "../data/load-book-home";
import { loadCompendium } from "../data/load-compendium";
import { BookSpoilers } from "../components/BookSpoilers";
import { NotFoundPage } from "./NotFoundPage";

interface Part {
  book: BookEntry;
  entry: CompendiumEntry;
}

interface Loaded {
  parts: Part[];
  imageUrl: string | undefined;
}

/** The race and vehicle addresses were always interchangeable, so they stay that way. */
const kindsAt = (kind: CompendiumKind): readonly CompendiumKind[] => (
  kind === "race" || kind === "vehicle" ? ["race", "vehicle"] : [kind]
);

const PEOPLE_HEADING: Partial<Record<CompendiumKind, string>> = {
  spell: "Known by",
  morph: "Acquired by",
  item: "Who had it",
};

async function loadEntry(
  books: readonly BookEntry[],
  kind: CompendiumKind,
  id: string,
): Promise<Loaded | null> {
  const found = await Promise.all(books.map(async (book): Promise<Part[]> => {
    const entry = (await loadCompendium(book.id))
      .find((item) => item.id === id && kindsAt(kind).includes(item.kind));
    return entry === undefined ? [] : [{ book, entry }];
  }));
  const parts = found.flat();
  if (parts.length === 0) {
    return null;
  }
  const first = parts[0]!;
  return { parts, imageUrl: (await loadPortraitUrls(first.book.id))(first.entry.imageStem) };
}

/** What each book tells us about one thing in a series' compendium. */
export function CompendiumPage({ kind }: { kind: CompendiumKind }) {
  const { seriesSlug, entryId } = useParams();
  const series = SERIES.find((item) => item.slug === seriesSlug);
  const [state, setState] = useState<Loaded | "loading" | "missing">("loading");

  useEffect(() => {
    if (series === undefined || entryId === undefined) {
      setState("missing");
      return;
    }
    setState("loading");
    void loadEntry(booksInSeries(series.id), kind, entryId)
      .then((loaded) => setState(loaded ?? "missing"));
  }, [series, kind, entryId]);

  const name = typeof state === "object" ? state.parts.at(-1)!.entry.name : null;
  useEffect(() => {
    if (name !== null && series !== undefined) {
      document.title = pageTitle(name, series.title);
    }
  }, [name, series]);

  if (state === "missing") {
    return <NotFoundPage />;
  }
  if (state === "loading") {
    return <TimelineSpinner label="Loading…" page />;
  }
  return (
    <div className="landing character-page">
      {state.imageUrl === undefined
        ? <span className="character-page__face" aria-hidden="true" />
        : <img className="character-page__face" src={state.imageUrl} alt="" />}
      <h1 className="landing-title">{name}</h1>
      {state.parts.map((part) => <BookPart key={part.book.id} {...part} />)}
    </div>
  );
}

function People({ book, entry }: Part) {
  const heading = PEOPLE_HEADING[entry.kind];
  const text = (value: string) => <CompendiumText text={value} bookId={book.id} skip={entry} />;
  return heading === undefined || entry.people.length === 0 ? null : (
    <div className="character-page__morphs">
      <h3 className="book-section-title">{heading}</h3>
      {entry.people.map((person) => (
        <div key={person.characterId} className="character-page__spell">
          <Link to={characterPath(book, person.characterId)}>
            <strong>{person.displayName}</strong>
          </Link>
          {person.note === undefined ? null : <p className="character-page__fact">{text(person.note)}</p>}
          {person.chapterId === null ? null : (
            <p className="character-page__from">From {describeChapterList([person.chapterId])}</p>
          )}
        </div>
      ))}
    </div>
  );
}

function BookPart({ book, entry }: Part) {
  const hidden = entry.details !== undefined || entry.chapterIds !== undefined
    || entry.fate !== undefined || entry.people.length > 0;
  const text = (value: string) => <CompendiumText text={value} bookId={book.id} skip={entry} />;
  return (
    <section className="character-page__book" aria-label={book.title}>
      <h2 className="book-section-title">{book.title}</h2>
      {entry.description === undefined ? null : <p className="character-page__fact">{text(entry.description)}</p>}
      {entry.namedBy === undefined ? null : <p className="character-page__fact">{text(entry.namedBy)}</p>}
      {entry.firstChapter === undefined ? null : (
        <p className="character-page__from">First seen in {describeChapterList([entry.firstChapter])}</p>
      )}
      {hidden ? (
        <BookSpoilers bookId={book.id} bookTitle={book.title}>
          {entry.details?.map((detail) => (
            <p key={detail.text} className="character-page__fact">
              {text(detail.text)}{" "}
              <span className="character-page__from">({describeChapterList(detail.chapterIds)})</span>
            </p>
          ))}
          {entry.chapterIds === undefined ? null : (
            <p className="character-page__from">Appears in {describeChapterList(entry.chapterIds)}</p>
          )}
          {entry.fate === undefined ? null : (
            <p className="character-page__from">
              {text(entry.fate.label)} in {describeChapterList([entry.fate.chapterId])}
            </p>
          )}
          <People book={book} entry={entry} />
        </BookSpoilers>
      ) : null}
      {entry.imageCredit === undefined ? null : <p className="character-page__from">{entry.imageCredit}</p>}
    </section>
  );
}
